
import Vue, { PropType } from 'vue';
import { db } from '@/firebase';
import {
	Avatar,
	Runnit,
	RunnitNode,
	RunnitNodeDef,
	RunnitNodeRun,
	RUNNITS_ACCESS_LEVEL,
	RUNNIT_NODE_RUN_STATE,
} from '@run-diffusion/shared';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';
import _isNil from 'lodash/isNil';
import _keys from 'lodash/keys';
import _find from 'lodash/find';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	SNACKBAR_STATUS,
} from '@/constants/constants';
import {
	RUNNIT_TOOL_SETTINGS_VALUES,
	RUNNIT_TOOL_SETTINGS_MENU_ITEMS,
} from '@/views/Runnits/constants';
import GlassButton from '@/components/base/GlassButton.vue';
import BaseGlassToggleButton, { GlassToggleOption } from '@/components/base/BaseGlassToggleButton.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import RunnitSettingsDrawer from '@/views/Runnits/RunnitSettingsDrawer.vue';
import ComplexBackground from '@/components/designElements/ComplexBackground.vue';
import ImageGalleryDialog from '@/components/ImageGallery/ImageGalleryDialog.vue';
import SelectedImagesDialog from '@/components/ImageGallery/SelectedImagesDialog.vue';
import RunnitToolsLibraryDialog from '@/views/Runnits/RunnitToolsLibraryDialog.vue';
import RunnitNodeSettingsDialog from '@/views/Runnits/RunnitNodeSettingsDialog.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import {
	MyBalanceMixin,
	RunnitRemixMixin,
	TeamUserMixin,
} from '@/mixins';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import RunnitImage from './RunnitImage.vue';
import RunnitActiveGenerationIndicator from './RunnitActiveGenerationIndicator.vue';
import RunnitDuplicateEditNodeDefInternalEditor
	from "@/views/Runnits/RunnitSettings/internalAdminOnly/RunnitDuplicateEditNodeDefInternalEditor.vue";
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import { ROUTER } from '@/router/constants';
import { isAtRunnitNodesLimit } from '@/utils/runnitNodesLimit';
import ToolOnboardingTopCard from '@/components/ImageGallery/ToolOnboardingTopCard.vue';
import moment from 'moment';

// Boards with more than this many tools initialize with every tool
// collapsed, so a large board doesn't open as an overwhelming wall of
// expanded galleries. Boards at or under it initialize fully expanded
// for at-a-glance visibility.
const MAX_NODES_FOR_AUTO_EXPAND_ON_INIT = 1;

export default Vue.extend({
	name: 'RunnitWorkflow',
	metaInfo () {
		return {
			title: this.currentRunnit ? this.currentRunnit.title : 'Loading...',
		};
	},
	props: {
		currentRunnit: {
			type: Object as PropType<Runnit>,
			required: true,
		},
		batchWizardOpen: { type: Boolean, default: false },
		confirmCloseBatchDrawer: {
			type: Function as PropType<() => Promise<boolean>>,
			default: null,
		},
	},
	data () {
		return {
			// Freeze static objects to prevent unnecessary reactivity
			RUNNIT_TOOL_SETTINGS_VALUES: Object.freeze(RUNNIT_TOOL_SETTINGS_VALUES),
			IMAGE_GALLERY_DIALOG_NAV: Object.freeze(IMAGE_GALLERY_DIALOG_NAV),
			NODE_TOOLBAR_ICON_SIZE: 32,
			NODE_FILTER_OPTIONS: Object.freeze<GlassToggleOption[]>([
				{ value: 'THIS_TOOL', icon: 'mdi-account-circle-outline', tooltip: 'This Tool' },
				{ value: 'FAVORITES', icon: 'mdi-heart-outline', tooltip: 'Favorites' },
			]),
			selectedFilterMap: {} as Record<string, string>,
			expandedNodesMap: {} as Record<string, boolean>,
			hasExpandedAllNodesOnInit: false,

			addingNewToolLoading: false,

			nodeSettingsDialog: {
				open: false,
				selectedNode: null as RunnitNode | null,
			},

			deleteToolDialog: {
				open: false,
				deleting: false,
				selectedNode: null as RunnitNode | null,
			},

			nodeDefInternalEditorDialog: {
				open: false,
				selectedNodeDef: null as RunnitNodeDef | null,
			},

			toolOnboardingDialog: {
				open: false,
				selectedNode: null as RunnitNode | null,
			},

			topSectionVisibilityMap: {} as Record<string, { isVisible: boolean, position: 'in' | 'above' | 'below' }>,
			// Single board-scoped real-time binding for in-flight runs only. The
			// number of concurrent pending runs is naturally small (a handful at
			// most), so this is cheap, bounded, and the live subscription cost is
			// minimal. Drives the chip's pending spinner and the floating
			// "above/below" indicator's "is the selected node generating?" gate.
			boardPendingRuns: [] as RunnitNodeRun[],
			// Per-node count of finished runs on this board. Populated by the
			// `@first-page-loaded` event each tool's `<image-gallery>` emits
			// after its existing initial fetch (page size 24). No new query —
			// we read off the side of the query the gallery already runs.
			//
			// `count` is the doc count on the first page; `hasMore` is true iff
			// the first page filled the limit. Chip displays "1".."23" exactly
			// or "24+" when hasMore. Beyond 24 isn't valuable for the chip's
			// purpose ("is there anything to look at?").
			nodeRunCountMap: {} as Record<string, number>,
			nodeRunHasMoreMap: {} as Record<string, boolean>,
		};
	},
	mixins: [
		RunnitsImageSelectMixin,
		TeamUserMixin,
		MyBalanceMixin,
		RunnitRemixMixin,
	],
	computed: {
		...mapState([
			'user',
			'runnitState',
			'runnitNodes',
			'runnitNodesMap',
			'triggers',
		]),
		...mapGetters([
			'runnitNodesLimitMap',
			'runnitNumNodesLimit',
		]),
		isAtNodesLimit (): boolean {
			return isAtRunnitNodesLimit((this.runnitNodes || []).length, this.runnitNumNodesLimit);
		},
		/**
		 * Whether a `.node-selected-gutter` marker is on screen for any node.
		 *
		 * That marker (the chevron badge whose tooltip says the settings drawer is open for this
		 * tool) is absolutely positioned to hang past its card's right edge, into the gap beside
		 * the drawer, so the tool list has to reserve that width or the marker renders underneath
		 * the drawer. The reservation is conditional rather than permanent: a board with no
		 * marker showing keeps the full width Slice 2 gave it.
		 *
		 * Deliberately mirrors the marker's own `v-if`, and must keep mirroring it: the `mdAndUp`
		 * half is what makes the reservation safe, because below `md` the settings drawer is a
		 * bottom sheet that reserves no width at all.
		 */
		showsSelectedNodeGutter (): boolean {
			return !!(
				this.$vuetify.breakpoint.mdAndUp &&
				this.runnitState.selectedNodeId &&
				this.runnitState.settingsDrawerOpen
			);
		},
		shouldExpandAllNodesOnInit (): boolean {
			// Small boards initialize fully expanded; larger boards
			// (> MAX_NODES_FOR_AUTO_EXPAND_ON_INIT tools) initialize collapsed.
			return (this.runnitNodes || []).length <= MAX_NODES_FOR_AUTO_EXPAND_ON_INIT;
		},
		selectedNode (): RunnitNode | null {
			return this.runnitState.selectedNodeId ? this.runnitNodesMap[this.runnitState.selectedNodeId] : null;
		},
		selectedNodeAvatar (): Avatar {
			return (this.selectedNode && typeof this.selectedNode.nodeDef !== 'string')
				? this._get(this.selectedNode, 'nodeDef.avatar')
				: null;
		},
		selectedNodeTitle (): string {
			return this._get(this.selectedNode, 'nodeDef.title') || this._get(this.selectedNode, 'title') || 'Tool';
		},
		isSelectedNodeGenerating (): boolean {
			if (!this.runnitState.selectedNodeId) return false;
			if (this.runnitState.isQueuingDraftRunnitNodeRun) return true;
			return this.pendingRunNodeIdsSet.has(this.runnitState.selectedNodeId);
		},
		selectedNodeViewportPosition (): 'in' | 'above' | 'below' {
			const id = this.runnitState.selectedNodeId;
			if (!id) return 'in';
			const v = this.topSectionVisibilityMap[id];
			if (!v) return 'in';
			if (v.isVisible) return 'in';
			return v.position;
		},
		showActiveGenerationIndicator (): boolean {
			// `mdAndUp` so tablets get the indicator too. xs/sm keep just the
			// inline chip — the floating UI would be cramped on those widths.
			return !!(
				this.$vuetify.breakpoint.mdAndUp &&
				this.runnitState.selectedNodeId &&
				this.runnitState.settingsDrawerOpen &&
				this.isSelectedNodeGenerating &&
				this.selectedNodeViewportPosition !== 'in'
			);
		},
		boundPropsBoardPendingRuns (): { runnitId: string } {
			return { runnitId: this._get(this.currentRunnit, 'id') };
		},
		pendingRunNodeIdsSet (): Set<string> {
			const set = new Set<string>();
			(this.boardPendingRuns || []).forEach((r: RunnitNodeRun) => {
				if (r && r.nodeId) set.add(r.nodeId);
			});
			// Also flag the selected node when a draft run is mid-queue
			// (transient state before the new run document appears in Firestore).
			if (this.runnitState.isQueuingDraftRunnitNodeRun && this.runnitState.selectedNodeId) {
				set.add(this.runnitState.selectedNodeId);
			}
			return set;
		},
		computedDescriptionTruncateLength (): number {
			return this.$vuetify.breakpoint.xsOnly ? 30 : 75;
		},
		canEditRunnit (): boolean {
			return !!(
				this.runnitState?.runnit &&
				(
					this.hasManageAllGenerationsPermission ||
					(
						this.runnitState.runnit.userId === this.user.id ||
						(
							[RUNNITS_ACCESS_LEVEL.SHARED, RUNNITS_ACCESS_LEVEL.RESTRICTED].includes(this.runnitState.runnit.accessLevel) &&
							this.hasShareRunnitAssetsPermission
						)
					)
				)
			);
		},
	},
	watch: {
		'$route.params.runnitId': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (this.$vuetify.breakpoint.xsOnly) {
					this.NODE_TOOLBAR_ICON_SIZE = 20;
				}
			},
		},
		runnitNodes: {
			immediate: true,
			handler (newVal: RunnitNode[], oldVal: RunnitNode[]) {
				const newlyCreatedNodes: RunnitNode[] = oldVal && oldVal.length
					? (newVal || []).filter(({ id }) => (
						!_find(oldVal || [], ['id', id])
					))
					: [];
				if (newlyCreatedNodes.length) {
					this.expandedNodesMap = {
						...this.expandedNodesMap,
						...newlyCreatedNodes.reduce((map: Record<string, boolean>, { id }) => ({
							...map,
							[id]: true,
						}), {}),
					};
					this.onSelectNode(newlyCreatedNodes[0].id);
				} else if (
					newVal &&
					newVal.length &&
					this.$vuetify.breakpoint.mdAndUp &&
					!this.runnitState.selectedNodeId
				) {
					// First time we've seen this board's nodes. Small boards
					// (<= MAX_NODES_FOR_AUTO_EXPAND_ON_INIT tools) default every
					// tool to expanded for at-a-glance visibility; larger boards
					// stay collapsed to avoid an overwhelming wall of galleries.
					// Either way, select the first tool.
					if (this.shouldExpandAllNodesOnInit) {
						this.expandedNodesMap = {
							...newVal.reduce((map: Record<string, boolean>, { id }) => ({
								...map,
								[id]: true,
							}), {}),
							...this.expandedNodesMap,
						};
					}
					this.onSelectNode(newVal[0].id);
				} else if (!newVal?.length && oldVal?.length) {
					this.onSelectNode(null);
				}
			},
		},
		runnitNodesLimitMap: {
			immediate: true,
			handler (newVal: Record<string, RunnitNode>, oldVal: Record<string, RunnitNode>) {
				if (newVal && !this.hasExpandedAllNodesOnInit) {
					this.expandAllNodes(newVal);
				}
			},
		},
		selectedNode: {
			immediate: true,
			handler (newVal: RunnitNode) {
				const newNodeDefTitle = newVal?.nodeDef?.title;
				const currentToolName = this.$route.query.toolName;
				if (newNodeDefTitle && currentToolName !== newNodeDefTitle) {
					this.$router.replace({
						...this.$route,
						query: {
							...this.$route.query,
							toolName: newNodeDefTitle,
						},
					});
				}
			},
		},
		'boundPropsBoardPendingRuns': {
			immediate: true,
			handler (newVal: { runnitId: string }, oldVal: { runnitId: string }) {
				if (this._get(newVal, 'runnitId') === this._get(oldVal, 'runnitId')) return;
				this.bindBoardPendingRuns(newVal.runnitId);
				// Board changed → clear any stale per-tool counts; the new
				// board's <image-gallery> mounts will repopulate via
				// onNodeRunsFirstPageLoaded.
				this.nodeRunCountMap = {};
				this.nodeRunHasMoreMap = {};
			},
		},
		'triggers.toggleRunnitWorkflowAllToolsTrigger': {
			immediate: false,
			handler (
				newVal: { expandAll?: boolean, collapseAll?: boolean, trigger: number },
				oldVal: { expandAll?: boolean, collapseAll?: boolean, trigger: number },
			) {
				if (newVal.trigger !== oldVal?.trigger) {
					if (newVal.expandAll) {
						// Open all tools
						this.expandAllNodes(this.runnitNodesLimitMap);
					} else if (newVal.collapseAll) {
						// Collapse all tools
						this.expandedNodesMap = {};
					}
				}
			},
		},
		// However a tool becomes the active tool (click, new tool, first load,
		// remix, feature tour), expand it if it is collapsed. Declared last so
		// the immediate run lands after expandAllNodes resets the map on init.
		'runnitState.selectedNodeId': {
			immediate: true,
			handler (newVal: string) {
				if (newVal && !this.expandedNodesMap[newVal]) {
					this.expandedNodesMap = {
						...this.expandedNodesMap,
						[newVal]: true,
					};
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
		]),
		isSelectedNodeDrawerOpen (nodeId: string) {
			return !!(
				this.runnitState.selectedNodeId &&
				this.runnitState.selectedNodeId === nodeId &&
				this.runnitState.settingsDrawerOpen
			);
		},
		isNodeExpanded (nodeId: string) {
			return !!(this.expandedNodesMap[nodeId]);
		},
		onNodeFilterChange (nodeId: string, value: string) {
			this.$set(this.selectedFilterMap, nodeId, value);
		},
		expandAllNodes (runnitNodesLimitMap: Record<string, RunnitNode>) {
			this.expandedNodesMap = {};
			this.hasExpandedAllNodesOnInit = true;
			// Larger boards (> MAX_NODES_FOR_AUTO_EXPAND_ON_INIT tools) initialize
			// collapsed — leave the map empty so every node defaults to collapsed.
			if (!this.shouldExpandAllNodesOnInit) return;
			_keys(runnitNodesLimitMap || {}).forEach((nodeId: string) => {
				if (runnitNodesLimitMap[nodeId]) this.expandedNodesMap[nodeId] = true;
			});
		},
		async onSelectNode (nodeId: string) {
			const prevId: string | null = this.runnitState.selectedNodeId;
			// Clicking the bar of the already active tool leaves selectedNodeId
			// unchanged, so its watcher will not expand a collapsed tool.
			if (nodeId && nodeId === prevId) this.ensureSelectedNodeExpanded();
			if (
				this.batchWizardOpen &&
				prevId &&
				nodeId &&
				prevId !== nodeId &&
				this.confirmCloseBatchDrawer
			) {
				const ok: boolean = await this.confirmCloseBatchDrawer();
				if (!ok) return;
			}
			this.updateRunnitState({
				selectedNodeId: nodeId,
				settingsDrawerOpen: !!nodeId,
			});
		},
		setSettingsDrawerOpen (isOpen: boolean) {
			this.updateRunnitState({
				settingsDrawerOpen: isOpen,
				...(!isOpen && {
					imageGalleryDialogOpen: false,
					promptGeneratorOpen: false,
					imageSelectOpen: false,
					inpaintingEditorOpen: false,
				}),
			});
		},
		toggleNodeExpanded (event: any, nodeId: string) {
			event.stopPropagation();
			this.expandedNodesMap[nodeId] = !this.expandedNodesMap[nodeId];
			this.$forceUpdate();
		},
		ensureSelectedNodeExpanded () {
			const nodeId = this.runnitState.selectedNodeId;
			if (nodeId && !this.expandedNodesMap[nodeId]) {
				this.expandedNodesMap[nodeId] = true;
				this.$forceUpdate();
			}
		},
		addAnotherNode () {
			if (this.isAtNodesLimit) return;
			this.setToolsLibraryDialogOpenState(true);
		},
		setToolsLibraryDialogOpenState (open: boolean) {
			this.updateRunnitState({
				toolsLibraryDialogOpen: open,
			});
		},
		toggleAdminNodeDefEditor (selectedNodeDef: RunnitNodeDef) {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: true,
				selectedNodeDef,
			};
		},
		onEditDialogClose () {
			this.nodeDefInternalEditorDialog = {
				...this.nodeDefInternalEditorDialog,
				open: false,
				selectedNodeDef: null,
			};
		},
		async onSelectedSettingMenuItem (selectedMenuItem: string, selectedNode: RunnitNode) {
			if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.VIEW_INFO) {
				if (
					this._get(selectedNode, 'nodeDef.onboardingData.enabledAt') &&
					moment(this._get(selectedNode, 'nodeDef.onboardingData.enabledAt').toMillis()).isBefore(moment())
				) {
					this.openToolOnboardingDialog(selectedNode);
				}
			} else if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.DELETE) {
				this.deleteToolDialog = {
					...this.deleteToolDialog,
					open: true,
					selectedNode,
				};
			} else if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.EDIT_INFO) {
				this.nodeSettingsDialog = {
					...this.nodeSettingsDialog,
					open: true,
					selectedNode,
				};
			} else if ([RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP, RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN].includes(selectedMenuItem)) {
				await this.moveNode(selectedMenuItem, selectedNode);
			}
		},
		onRunnitNodeSettingsClose () {
			this.nodeSettingsDialog = {
				open: false,
				selectedNode: null,
			};
		},
		onDeleteCancelReset () {
			this.deleteToolDialog = {
				open: false,
				deleting: false,
				selectedNode: null,
			};
		},
		async handleDeleteNodeDefConfirm (closeAndResetCallback: Function) {
			try {
				this.deleteToolDialog = {
					...this.deleteToolDialog,
					deleting: true,
				};
				const { id, runnitId } = this.deleteToolDialog.selectedNode;
				const runnitNodeRef = db.doc(`runnits/${runnitId}/runnitNodes/${id}`);

				await runnitNodeRef.update({
					deletedAt: new Date(),
				});
				// If the deleted node was selected, clear selection and close drawer before
				// Firestore binding removes the node so the drawer doesn't render with selectedNode === null
				if (this.runnitState.selectedNodeId === id) {
					this.onSelectNode(null);
				}
			} catch (e) {
				console.error(e);

				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error! Problem deleting this tool from the Board, please reach out to report issues by going to Resources -> Help from the menu options`,
					show: true,
				});
			} finally {
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
				this.onDeleteCancelReset();
			}
		},
		moveNode (selectedMenuItem: string, node: RunnitNode) {
			let nodeIndex: number = null;
			const nodeIdsList: string[] = (this.runnitNodes || []).map(({ id }, index: number) => {
				if (id === node.id) nodeIndex = index;
				return id;
			});

			// Can't move up the first node or move down the last node
			if (
				_isNil(nodeIndex) ||
				(
					selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP &&
					nodeIndex <= 0
				) ||
				(
					selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN &&
					nodeIndex >= this.runnitNodes.length - 1
				)
			) {
				return;
			}

			// Swap the node with the one before it or after it
			if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP) {
				[nodeIdsList[nodeIndex - 1], nodeIdsList[nodeIndex]] = [nodeIdsList[nodeIndex], nodeIdsList[nodeIndex - 1]];
			} else if (selectedMenuItem === RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN) {
				[nodeIdsList[nodeIndex], nodeIdsList[nodeIndex + 1]] = [nodeIdsList[nodeIndex + 1], nodeIdsList[nodeIndex]];
			}

			// Set sortOrder
			return db.runTransaction(async (transaction) => {
				nodeIdsList.forEach((id: string, index: number) => {
					transaction.update(
						db.doc(`runnits/${node.runnitId}/runnitNodes/${id}`),
						{ sortOrder: index },
					);
				});
			});
		},
		determineMenuItems (node: RunnitNode, nodeIndex: string | number) {
			const onlyOneNodeExists: boolean = this.runnitNodes.length === 1;
			let menuItems: any[] = [...RUNNIT_TOOL_SETTINGS_MENU_ITEMS].filter(({ value, needsCanEditRunnitPermission }) => {
				if (value === RUNNIT_TOOL_SETTINGS_VALUES.VIEW_INFO) {
					return !!(
						this._get(node, 'nodeDef.onboardingData.enabledAt') &&
						moment(this._get(node, 'nodeDef.onboardingData.enabledAt').toMillis()).isBefore(moment())
					);
				}
				return !!(
					!needsCanEditRunnitPermission ||
					this.canEditRunnit
				);
			});
			if (onlyOneNodeExists) return menuItems;

			if (!menuItems.length) return menuItems;
			const nextNode: RunnitNode = this._get(this.runnitNodes, `[${+nodeIndex + 1}]`) || null;
			const isNodeInRunnitNodesLimitMap: boolean = !!this.runnitNodesLimitMap[node.id];
			const isNextNodeInRunnitNodesLimitMap: boolean = !!(nextNode && this.runnitNodesLimitMap[nextNode.id]);
			const canMoveUp: boolean = +nodeIndex > 0;
			const canMoveDown: boolean = +nodeIndex < (this.runnitNodes.length - 1);

			// Find the edit info item and its index
			const editInfoItem = _find(menuItems, ['value', RUNNIT_TOOL_SETTINGS_VALUES.EDIT_INFO]);
			const insertIndex: number = editInfoItem ? menuItems.indexOf(editInfoItem) + 1 : menuItems.length;
			let lastId: number = menuItems[menuItems.length - 1].id;

			// Insert move up / down controls after EDIT_INFO
			if (this.canEditRunnit && canMoveUp) {
				lastId += 1;
				menuItems.splice(insertIndex, 0, {
					id: lastId,
					icon: 'mdi-chevron-up',
					label: 'Move Tool Up',
					description: '',
					value: RUNNIT_TOOL_SETTINGS_VALUES.MOVE_UP,
					needsCanEditRunnitPermission: true,
					disabled: !isNodeInRunnitNodesLimitMap,
				});
			}
			if (this.canEditRunnit && canMoveDown) {
				lastId += 1;
				menuItems.splice(insertIndex + +canMoveUp, 0, {
					id: lastId,
					icon: 'mdi-chevron-down',
					label: 'Move Tool Down',
					description: '',
					value: RUNNIT_TOOL_SETTINGS_VALUES.MOVE_DOWN,
					needsCanEditRunnitPermission: true,
					disabled: !(isNodeInRunnitNodesLimitMap && isNextNodeInRunnitNodesLimitMap),
				});
			}
			return menuItems;
		},
		handleRemixClick (nodeDef, nodeRunId, nodeRun, resultUuid) {
			if (!this.user) {
				const currentPath: string = this.$router.resolve({
					name: ROUTER.RUNNIT_REMIX,
					query: {
						rnrid: nodeRunId,
						ruuid: resultUuid,
						autoRemix: true,
					},
				}).href;
				this.$router.push({
					name: ROUTER.LOGIN,
					query: { redirect: currentPath }
				});
			} else {
				this.openToolWithSameInputs(nodeDef, nodeRun, nodeRunId, nodeRun ? nodeRun.runnitId : null, nodeRun ? nodeRun.nodeId : null);
			}
		},
		openToolOnboardingDialog (node: RunnitNode) {
			this.toolOnboardingDialog = {
				open: true,
				selectedNode: node,
			};
		},
		closeToolOnboardingDialog () {
			this.toolOnboardingDialog = {
				open: false,
				selectedNode: null,
			};
		},
		topSectionIntersectHandler (entries: IntersectionObserverEntry[], nodeId: string) {
			if (!entries || !entries.length) return;
			const entry = entries[0];
			let position: 'in' | 'above' | 'below' = 'in';
			if (!entry.isIntersecting) {
				const rb = entry.rootBounds;
				const cr = entry.boundingClientRect;
				if (rb && cr.bottom <= rb.top) position = 'above';
				else if (rb && cr.top >= rb.bottom) position = 'below';
				else position = cr.top < 0 ? 'above' : 'below';
			}
			this.topSectionVisibilityMap = {
				...this.topSectionVisibilityMap,
				[nodeId]: { isVisible: !!entry.isIntersecting, position },
			};
		},
		formatNodeRunCount (nodeId: string): string {
			const count: number = this.nodeRunCountMap[nodeId] || 0;
			// `hasMore` means the gallery's first page (24) filled — there's
			// at least one more page. Show "24+" instead of an exact number
			// since "is there content here?" is all the chip needs to answer.
			if (this.nodeRunHasMoreMap[nodeId]) return `${count}+`;
			return String(count);
		},
		onNodeRunsFirstPageLoaded ({ nodeId, count, hasMore }: { nodeId: string, count: number, hasMore: boolean }) {
			if (!nodeId) return;
			this.nodeRunCountMap = { ...this.nodeRunCountMap, [nodeId]: count };
			this.nodeRunHasMoreMap = { ...this.nodeRunHasMoreMap, [nodeId]: hasMore };
		},
		async bindBoardPendingRuns (runnitId: string) {
			// $unbind is a safe no-op if the key isn't currently bound, and $bind
			// auto-unbinds on rebind anyway — so we only need to call it explicitly
			// for the navigate-away (null runnitId) case below.
			if (!runnitId) {
				this.$unbind('boardPendingRuns');
				this.boardPendingRuns = [];
				return;
			}
			try {
				// index created: runnitNodeRuns: deletedAt Ascending runnitId Ascending state Ascending initAt Descending __name__ Descending
				const ref = db.collection('runnitNodeRuns')
					.where('state', 'in', [RUNNIT_NODE_RUN_STATE.INIT, RUNNIT_NODE_RUN_STATE.QUEUED])
					.where('deletedAt', '==', null)
					.where('runnitId', '==', runnitId)
					.limit(1);
				await this.$bind('boardPendingRuns', ref, get$bindFirestoreOptions());
			} catch (e) {
				console.error('bindBoardPendingRuns failed', e);
			}
		},
		scrollToSelectedNode () {
			const id: string = this.runnitState.selectedNodeId;
			if (!id) return;
			// `:ref="`nodeTop_${node.id}`"` inside a v-for yields an array; take the
			// first (and only) element.
			const refEntry: HTMLElement | HTMLElement[] = this._get(this.$refs, `nodeTop_${id}`);
			const el: HTMLElement = Array.isArray(refEntry) ? refEntry[0] : refEntry;
			if (!el) return;
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		},
	},
	components: {
		RunnitDuplicateEditNodeDefInternalEditor,
		LoadingState,
		RunnitNodeSettingsDialog,
		DoubleConfirmDangerDialog,
		BaseStyledMenu,
		BaseGlassToggleButton,
		GlassButton,
		ImageGallery,
		RunnitToolsLibraryDialog,
		EmptyState,
		ComplexBackground,
		ImageGalleryDialog,
		SelectedImagesDialog,
		RunnitSettingsDrawer,
		RunnitImage,
		ToolOnboardingTopCard,
		RunnitActiveGenerationIndicator,
	},
});
